/* @flow */
import * as React from 'react';
import cx from 'classnames';
import gql from 'graphql-tag';
import MaskedInput from 'react-input-mask';
import invariant from 'invariant';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { formatAsPhoneNumber, stripFormatting, ensureLeading1 } from 'lib/phoneNumbers';
import { mountModal } from 'lib/modal.js';
import { showToast } from 'reducers/toasts';
import DialerDirectAddModal from 'phoneCalls/components/DialerDirectAddModal.jsx';
import Formsy from 'lib/Formsy';
import Glyph from 'components/Glyph.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import MediaObject from 'components/MediaObject.jsx';
import PhoneCallModal from 'phoneCalls/components/PhoneCallModal.jsx';
import RemindAPI from 'lib/RemindAPI';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import UserAvatar from 'users/components/UserAvatar.jsx';

import type { ReachabilityInfo } from 'components/ReachabilityBadge.jsx';

import './styles/PhoneNumberDialer.scss';
import 'components/styles/Tokenizer.scss';

type Props = {
  onInputChange: Function,
  inputValue: string,
  onRequestHide?: Function,
  showToast: (text: string, kind: 'success' | 'error') => any,
};

type State = {
  focused: boolean,
  valid: boolean,
  complete: boolean,
  user: ?{
    id: Backbone$Id,
    uuid: string,
    name: ?string,
    color: ?string,
    initials: ?string,
    profilePictureUrl: ?string,
    is_or_inferred_child: ?boolean,
    reachabilityInfo: ReachabilityInfo,
  },
  loading: boolean,
};

export const QUERY = gql`
  query getConnectedUsersBySmsAddresses($smsAddresses: [String!]) {
    me {
      id
      uuid
      connectedUsers(smsAddresses: $smsAddresses) {
        id
        uuid
        color
        initials
        name
        is_or_inferred_child
        profilePictureUrl
        reachabilityInfo {
          status
          reasonShort
          reasonLong
        }
      }
    }
  }
`;

class PhoneNumberDialer extends React.Component<Props, State> {
  state: State = {
    focused: true,
    valid: true,
    complete: false,
    user: null,
    loading: false,
  };

  componentWillMount() {
    const { inputValue } = this.props;
    this.handlePhoneNumberChange(ensureLeading1(inputValue));
  }

  findUsers = (smsAddress: string) => {
    this.setState({ loading: true });
    RemindAPI.execute({
      query: QUERY,
      variables: { smsAddresses: [smsAddress] },
    })
      .then(
        data => {
          // XXX should be enforced server side
          invariant(data.me.connectedUsers.length === 1, 'only one user per sms device');
          this.setState({ user: data.me.connectedUsers[0], loading: false });
        },
        () => {
          // case where no user that matches the sms device
          this.setState({ user: null, loading: false });
        }
      )
      .catch(error => {
        console.error('searching for users failed', error);
        this.props.showToast('Error searching for users, ' + error.message, 'error');
      });
  };

  handleInputChange = (event: *) => {
    const value = stripFormatting(event.target.value);

    this.handlePhoneNumberChange(value);
    this.props.onInputChange(value);
  };

  handlePhoneNumberChange = (phone: string) => {
    const value = stripFormatting(phone);

    const completeNumber = value.length === 11; // 1 510 555 1212

    if (completeNumber) {
      const valid = Formsy.customValidators.couldPlausiblyBeANorthAmericanPhoneNumber('', value);
      this.setState({ valid });

      if (valid) {
        this.findUsers(value);
      }
    }

    this.setState({ complete: completeNumber });
  };
  handleBlur = () => {
    this.setState({ focused: false });
  };

  handleFocus = () => {
    this.setState({ focused: true });
  };

  renderAdd = () => {
    const addNumber = () => {
      mountModal(
        <DialerDirectAddModal
          number={this.props.inputValue}
          // $FlowFixMe null incompatible
          hideParent={this.props.onRequestHide}
        />,
        this
      );
    };
    return (
      <div>
        <Row size="small">
          <div className="PhoneNumberDialer-action" onClick={addNumber}>
            <Glyph size="xlarge" name="plus-circle-stroke" color="brand" />
            <Text size="medium" color="brand">
              Add {formatAsPhoneNumber(ensureLeading1(this.props.inputValue))} to Remind to call
              them
            </Text>
          </div>
        </Row>
        <Row size="small">
          <Text size="small" color="thunder">
            By proceeding, I confirm that the individual I am calling is over the age of 13.
          </Text>
        </Row>
      </div>
    );
  };

  renderCallUser() {
    const { user } = this.state;
    invariant(user, 'must have a user result before displaying call link');
    const startCall = (uuid: string) => {
      mountModal(<PhoneCallModal calleeUuid={uuid} />, this);
      if (this.props.onRequestHide) {
        this.props.onRequestHide();
      }
    };
    if (user.is_or_inferred_child) {
      return (
        <div className="PhoneNumberDialer-message">
          <Text color="thunder">
            Voice calls are only avaliable for students over the age of 13.
          </Text>
        </div>
      );
    }
    return (
      <div className="PhoneNumberDialer-action" onClick={() => startCall(user.uuid)} key={user.id}>
        <div>
          <MediaObject center>
            <UserAvatar user={user} size="medium" />
            <Text truncate color="pitch">
              {user.name}
            </Text>
          </MediaObject>
        </div>
      </div>
    );
  }

  renderComplete = () => {
    if (this.state.loading) {
      return <LoadingIndicator />;
    } else {
      return this.state.user ? this.renderCallUser() : this.renderAdd();
    }
  };

  render() {
    const { inputValue } = this.props;
    return (
      <div className="PhoneNumberDialer">
        <div
          className={cx('Tokenizer', {
            'Tokenizer-focused': this.state.focused,
            'PhoneNumberDialer-invalid': !this.state.valid,
          })}>
          <div className={'Tokenizer-prompt-container'}>
            <Text color="thunder">To:</Text>
          </div>
          <div className={'Tokenizer-input-container'}>
            <MaskedInput
              className={cx('PhoneNumberDialer-input', {
                'Tokenizer-input-has-value': !!inputValue,
              })}
              mask="9 (999) 999-9999"
              maskChar={null}
              type="text"
              value={ensureLeading1(inputValue)}
              size={inputValue.length || 1}
              autoFocus
              onChange={this.handleInputChange}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
            />
          </div>
        </div>
        <Row size="small" />
        {this.state.valid && this.state.complete && this.renderComplete()}
      </div>
    );
  }
}

function mapDispatchToProps(dispatch: Function) {
  // $FlowIssue
  return bindActionCreators({ showToast }, dispatch);
}

export { PhoneNumberDialer };
export default connect(
  undefined,
  mapDispatchToProps
)(PhoneNumberDialer);
